import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form } from "formik";
import { Alert, Button, Container, Row, Col } from "reactstrap";
import { EditProvider } from '../../Services/EditContext';

const getValidationErrors = (errors, path = '', name = '') => {
    if (typeof errors === 'string') {
        const separatorIndex = path.lastIndexOf(' / ');
        const context = separatorIndex < 0 ? '' : path.slice(0, separatorIndex);
        return [{ path, context, message: errors, name }];
    }
    if (!errors || typeof errors !== 'object') {
        return [];
    }
    return Object.keys(errors).flatMap(key => {
        const label = key === 'routes' ? 'Route' : key === 'manifest' ? 'Passenger'
            : key.replace(/([A-Z])/g, ' $1').replace(/^./, character => character.toUpperCase());
        const nextPath = Array.isArray(errors) ? `${path} ${Number(key) + 1}`
            : path ? `${path} / ${label}` : label;
        const nextName = Array.isArray(errors) ? `${name}[${key}]` : name ? `${name}.${key}` : key;
        return getValidationErrors(errors[key], nextPath, nextName);
    });
};

// Keeps errors in the order their fields are rendered on the page. Errors without a rendered field stay at the end.
const sortByFieldPosition = (validationErrors, fieldNames) => {
    const rank = error => {
        const index = fieldNames.indexOf(error.name);
        return index < 0 ? fieldNames.length : index;
    };
    return [...validationErrors].sort((a, b) => rank(a) - rank(b));
};

const SubmitFormWrapper = ({ edit, children, handleSubmit, isSubmitting, isValidating, submitCount, errors, setHandleSubmit, setSubmitting }) => {
    const summaryRef = useRef(null);
    const formRef = useRef(null);
    const focusedSubmitCount = useRef(0);
    const [fieldNames, setFieldNames] = useState([]);

    // Field order on the page, refreshed when the errors or submit count change
    useEffect(() => {
        const names = [...(formRef.current?.querySelectorAll('[data-error-name]') || [])].map(el => el.dataset.errorName);
        setFieldNames(prev => prev.join() === names.join() ? prev : names);
    }, [errors, submitCount]);

    useEffect(() => {
        if (setHandleSubmit) {
            setHandleSubmit(handleSubmit);
        }
    }, [handleSubmit, setHandleSubmit]);

    useEffect(() => {
        if (setSubmitting) {
            setSubmitting(isSubmitting);
        }
    }, [isSubmitting, setSubmitting]);

    const validationErrors = edit && submitCount > 0 && !isSubmitting && !isValidating
        ? sortByFieldPosition(getValidationErrors(errors), fieldNames) : [];
    const hasValidationErrors = validationErrors.length > 0;

    useEffect(() => {
        if (submitCount === 0) {
            focusedSubmitCount.current = 0;
        }
        if (hasValidationErrors && focusedSubmitCount.current !== submitCount && summaryRef.current) {
            focusedSubmitCount.current = submitCount;
            summaryRef.current.focus({ preventScroll: true });
            summaryRef.current.scrollIntoView?.({ block: 'nearest' });
        }
    }, [hasValidationErrors, submitCount]);

    return <EditProvider edit={edit}>
        <Form ref={formRef}>
            <React.Fragment>                
                {hasValidationErrors ? <Alert color='danger' role='alert' innerRef={summaryRef} tabIndex={-1}>
                    <strong>Unable to save. Please correct the following:</strong>
                    <ul className='mb-0'>
                        {validationErrors.map(error => <li key={error.path}>
                            {error.context ? `${error.context}: ` : ''}{error.message}
                        </li>)}
                    </ul>
                </Alert> : null}
                {children}
                {edit && !setHandleSubmit ? <Container><Row><Col><Button type='submit' disabled={isSubmitting}>Submit</Button></Col></Row></Container> : null}
            </React.Fragment>
        </Form>
    </EditProvider>
}
const FormContainer = ({ initialValues, validationSchema, edit, setHandleSubmit, onSubmit, children, setSubmitting }) => {
    return (<Formik initialValues={{ ...initialValues }}
        onSubmit={onSubmit}

        enableReinitialize={true}
        validateOnMount={true}
        validationSchema={validationSchema}>
        {(props) => {
            return <SubmitFormWrapper {...props} edit={edit} children={children} setHandleSubmit={setHandleSubmit} setSubmitting={setSubmitting} />
        }}</Formik>)
};

export default FormContainer;